// Slå upp (owner 2026-10-29): scan a location and see what stands there; scan a kolli or a
// pallet and see where it stands and what it holds. Read-only, no flow, no events. Reached
// from the menu item or by scanning straight on the menu.

import { identityKey, parseScan, type GS1Elements } from "@trail/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { type BundleProduct, Api, type ContainerSummary } from "../api/client.js";
import { CameraView } from "../CameraView.js";
import { fmtQty, t } from "../i18n.js";
import { buildRegistry, cacheBundle, loadCachedBundle, type Registry, findLocation } from "../receive/registry.js";
import { isGs1Symbology, type CodeState } from "../scan/camera.js";
import { feedback, unlockAudio } from "../scan/feedback.js";
import { attachWedge } from "../scan/wedge.js";
import { Actions, Banner, Btn, Prompt, Rows, ScanBar, Strip, focusScan, type BannerData, type RowData } from "../ui/components.jsx";

type Shown =
  | { kind: "location"; code: string; path: string; rows: RowData[]; pallets: number; kolli: number }
  | { kind: "unit"; title: string; sub: string; rows: RowData[]; where: string }
  /** An article (owner 2026-11-03: "slå upp ett artikelnummer"): where it stands and how much. */
  | { kind: "product"; title: string; sub: string; rows: RowData[]; total: string; places: number };

const shortSscc = (sscc: string) => `${sscc.slice(0, 4)}…${sscc.slice(-4)}`;

export function Lookup({ onClose, initial }: { onClose: () => void; initial?: string | undefined }) {
  // The registry resolves location codes and product names; a device that has not run a
  // flow yet has no cached bundle, so fetch it and hold a scan until it is here.
  const registry = useRef<Registry | null>(null);
  const [ready, setReady] = useState(false);
  const pendingScan = useRef<string | null>(null);
  useEffect(() => {
    const cached = loadCachedBundle();
    if (cached) { registry.current = buildRegistry(cached); setReady(true); }
    Api.bundle().then((b) => { cacheBundle(b); registry.current = buildRegistry(b); setReady(true); }).catch(() => undefined);
  }, []);
  const inputRef = useRef<HTMLInputElement>(null);
  const [shown, setShown] = useState<Shown | null>(null);
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState(false);

  const productName = (id: string | null) => (id ? registry.current?.bundle.products.find((p) => p.id === id)?.name ?? t("label.unknownProduct") : t("label.unknownProduct"));
  const locationCode = (id: string | null) => (id ? registry.current?.bundle.locations.find((l) => l.id === id)?.code ?? "—" : "—");
  const kolliRow = (c: ContainerSummary, level?: RowData["level"]): RowData =>
    ({ k: productName(c.product_id), small: [c.kind === "loose" ? null : c.lot_number, c.state === "picked" ? t("lookup.picked") : null].filter(Boolean).join(" · "), q: fmtQty(c.quantity, c.uom), level });
  /** A pallet is a head row with every kolli on it listed under it (owner 2026-10-29: "the
   *  real contents, the cartons, should be listed under"). A loose kolli is one plain row. */
  const rowsOf = (c: ContainerSummary, children: ContainerSummary[]): RowData[] =>
    c.kind === "logistic-unit"
      ? [
          { k: `${t("unit.pallet")} ${c.sscc ? shortSscc(c.sscc) : ""}`, small: t("rows.kolli", { n: children.length }), q: c.state === "picked" || c.state === "packed" ? t("lookup.outbound") : "", level: "head" },
          ...children.map((k) => kolliRow(k, "sub")),
        ]
      : [kolliRow(c)];

  const show = useCallback(async (raw: string) => {
    const reg = registry.current;
    if (!reg) { pendingScan.current = raw; return; }
    const aim = /^\]\w\w/.exec(raw)?.[0] ?? null;
    const text = aim ? raw.slice(3) : raw;
    let elements: GS1Elements | null = null;
    if (!(aim && !isGs1Symbology(raw))) {
      try { elements = await parseScan(raw); } catch { elements = null; }
    }
    setBusy(true);
    try {
      const loc = (elements?.ai["254"] ? reg.locationByGln.get(elements.ai["254"]) : findLocation(reg, text)) ?? null;
      if (loc) {
        const rows = await Api.locationContents(loc.id);
        const withKids = (await Promise.all(rows.map(async (c) => rowsOf(c, c.kind === "logistic-unit" ? (await Api.container(c.container_id).catch(() => null))?.children ?? [] : [])))).flat();
        setShown({ kind: "location", code: loc.code, path: loc.path, rows: withKids, pallets: rows.filter((c) => c.kind === "logistic-unit").length, kolli: rows.filter((c) => c.kind !== "logistic-unit").length });
        setBanner(null);
        feedback("accepted");
        return;
      }
      // An article number typed (with or without Visma's leading zeros), or a barcode that names only
      // the product: the article's stock per place.
      const typed = text.trim().toLowerCase();
      const bare = typed.replace(/^0+/, "");
      const bySku = typed ? reg.bundle.products.find((p) => p.sku.toLowerCase() === typed) ?? (bare ? reg.bundle.products.filter((p) => p.sku.toLowerCase().replace(/^0+/, "") === bare) : []).reduce<BundleProduct | null>((one, p, _i, all) => (all.length === 1 ? p : one), null) : null;
      const byGtin = !bySku && elements?.gtin && !elements.lot && !elements.sscc ? reg.byGtin.get(elements.gtin)?.product ?? null : null;
      const product = bySku ?? byGtin;
      if (product) {
        const stock = (await Api.stock(product.id)).filter((r) => r.kind !== "logistic-unit" && (r.state === "received" || r.state === "stored" || r.state === "picked") && Number(r.quantity) > 0);
        const byPlace = new Map<string, typeof stock>();
        for (const r of stock) { const k = r.location_code ?? "—"; byPlace.set(k, [...(byPlace.get(k) ?? []), r]); }
        const rows: RowData[] = [...byPlace.entries()].sort((a, b) => a[0].localeCompare(b[0], "sv", { numeric: true })).map(([code, rs]) => ({
          k: code,
          // Bulk rows carry a technical lot the operator never sees (owner 2026-11-03): the batch numbers shown are the real ones.
          small: [...new Set(rs.filter((r) => r.kind !== "loose").map((r) => r.lot_number).filter((x): x is string => !!x))].join(", ") || (rs.some((r) => r.state === "picked") ? t("lookup.picked") : ""),
          q: fmtQty(rs.reduce((sum, r) => sum + Number(r.quantity), 0), rs[0]!.uom),
        }));
        const total = stock.reduce((sum, r) => sum + Number(r.quantity), 0);
        setShown({ kind: "product", title: product.name, sub: product.sku, rows, total: fmtQty(total, stock[0]?.uom ?? product.baseUom), places: byPlace.size });
        setBanner(null);
        feedback("accepted");
        return;
      }
      const key = elements?.sscc ? `sscc:00=${elements.sscc}` : elements ? identityKey("gtin+lot", elements) : null;
      if (!key) {
        feedback("rejected");
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("msg.unknownCode", { raw: text.slice(0, 40) }) });
        return;
      }
      const hits = await Api.containersByIdentity(key);
      if (hits.length === 0) {
        feedback("rejected");
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.notFound") });
        return;
      }
      const c = hits[0]!;
      const full = await Api.container(c.container_id).catch(() => null);
      const kids = full?.children ?? [];
      const parent = c.parent_id ? await Api.container(c.parent_id).catch(() => null) : null;
      const where = [locationCode(c.location_id), parent?.sscc ? `${t("unit.pallet")} ${shortSscc(parent.sscc)}` : null].filter(Boolean).join(" · ");
      setShown({
        kind: "unit",
        title: c.kind === "logistic-unit" ? `${t("unit.pallet")} ${c.sscc ? shortSscc(c.sscc) : ""}` : productName(c.product_id),
        sub: c.kind === "logistic-unit" ? t("rows.kolli", { n: kids.length }) : [c.kind === "loose" ? null : c.lot_number, fmtQty(c.quantity, c.uom), c.state === "picked" ? t("lookup.picked") : null].filter(Boolean).join(" · "),
        rows: kids.map((k) => kolliRow(k)),
        where,
      });
      setBanner(hits.length > 1 ? { kind: "warn", label: t("banner.acceptedDeviation"), message: t("putaway.several", { n: hits.length }) } : null);
      feedback("accepted");
    } catch (e) {
      feedback("rejected");
      setBanner({ kind: "bad", label: t("banner.rejected"), message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (initial) void show(initial);
  }, [initial, show]);
  useEffect(() => {
    if (ready && pendingScan.current) { const raw = pendingScan.current; pendingScan.current = null; void show(raw); }
  }, [ready, show]);
  useEffect(() => {
    const el = inputRef.current;
    if (!el || camera) return;
    focusScan(el);
    return attachWedge(el, { onScan: (d) => void show(d) });
  }, [show, camera]);

  const onCameraCode = useCallback(async (data: string): Promise<CodeState> => {
    await show(data);
    setCamera(false);
    return "accepted";
  }, [show]);
  if (camera) return <CameraView onCode={onCameraCode} onClose={() => setCamera(false)} />;

  return (
    <div className="screen" onClick={() => { unlockAudio(); focusScan(inputRef.current); }}>
      <Strip left={<b>{t("menu.lookup")}</b>} middle={shown?.kind === "location" ? shown.code : shown?.kind === "unit" ? shown.where : shown?.kind === "product" ? shown.sub : undefined} online={navigator.onLine} queued={0} stuck={0} />
      <div className="body">
        <Prompt title={t("lookup.title")} sub={t("lookup.sub")} />
        <ScanBar inputRef={inputRef} placeholder={t("lookup.scan")} onCamera={() => { unlockAudio(); setCamera(true); }} />
        {banner && <Banner {...banner} />}
        {busy && <p className="hint">{t("common.loading")}</p>}
        {shown?.kind === "location" && (
          <>
            <div className="proposal"><div className="loc">{shown.code}</div><div className="why">{shown.path.split("/").slice(0, -1).join(" · ")} · {t("lookup.here", { pallets: shown.pallets, kolli: shown.kolli })}</div></div>
            {shown.rows.length === 0 ? <div className="label"><div className="empty">{t("lookup.empty")}</div></div> : <Rows rows={shown.rows} />}
          </>
        )}
        {shown?.kind === "product" && (
          <>
            <div className="label">
              <div className="head"><b>{shown.title}</b><span>{shown.sub}</span></div>
              <div className="rows"><div className="cell"><div className="t">{t("lookup.inStock")}</div><div className="v">{shown.total}</div></div><div className="cell"><div className="t">{t("lookup.places")}</div><div className="v">{shown.places}</div></div></div>
            </div>
            {shown.rows.length === 0 ? <div className="label"><div className="empty">{t("lookup.noStock")}</div></div> : <Rows title={t("lookup.where")} rows={shown.rows} />}
          </>
        )}
        {shown?.kind === "unit" && (
          <>
            <div className="label">
              <div className="head"><b>{shown.title}</b><span>{shown.sub}</span></div>
              <div className="rows"><div className="cell wide"><div className="t">{t("lookup.where")}</div><div className="v">{shown.where || "—"}</div></div></div>
            </div>
            {shown.rows.length > 0 && <Rows title={t("side.onPallet")} rows={shown.rows} />}
          </>
        )}
      </div>
      <Actions><Btn kind="quiet" icon="back" onClick={onClose}>{t("common.menu")}</Btn></Actions>
    </div>
  );
}
